/**
 * Row-level DQX summary: checked / passing / failing rows and breakdown by contract rule_type.
 */

import {
  buildContractRuleLookup,
  normalizeDqxRuleName,
  type DqxContractRuleMeta,
} from "@/services/dqxContractRulesLookup";
import {
  getQuarantineRowField,
  parseQuarantineRulesArray,
  resolveQuarantineWorksheetRowIndex,
} from "@/services/dqxQuarantineMapper";

/** Display buckets aligned with product terminology. */
export const DQX_SUMMARY_CHECK_TYPE_ORDER = [
  "data type",
  "mandatory",
  "summation",
  "conditional",
  "duplicates",
  "pattern",
  "reference list",
  "other",
] as const;

export type DqxSummaryCheckTypeBucket = (typeof DQX_SUMMARY_CHECK_TYPE_ORDER)[number];

export interface DqxRowLevelSummary {
  totalRowsChecked: number;
  rowsPassingAllChecks: number;
  rowsWithAtLeastOneFailure: number;
  /** Failing row count per check-type bucket (a row may appear in multiple buckets). */
  failingRowsByCheckType: Record<DqxSummaryCheckTypeBucket, number>;
}

function normalizeRuleType(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, " ");
}

/**
 * Map contract YAML `rule_type` strings to UI buckets.
 */
export function mapContractRuleTypeToBucket(
  ruleType: string | undefined,
  opts?: { ruleName?: string; checkFunction?: string }
): DqxSummaryCheckTypeBucket {
  if (!ruleType?.trim()) {
    const ruleName = opts?.ruleName?.trim().toLowerCase() ?? "";
    const checkFunction = opts?.checkFunction?.trim().toLowerCase() ?? "";
    // Recognizes Tier 1/2 checks by their native names: the retired Spark/DQX engine's
    // logical_type_*/logical_type_validation (old quarantine records), and DuckDB's own
    // template_id as `function`.
    if (
      ruleName.startsWith("logical_type_") ||
      checkFunction === "logical_type_validation" ||
      checkFunction === "valid_date" ||
      checkFunction === "valid_number" ||
      checkFunction === "valid_boolean"
    ) {
      return "data type";
    }
    // Falls back here when metadata is unavailable for the specific rule name.
    if (checkFunction === "is_in_list" || checkFunction === "in_reference_list") {
      return "reference list";
    }
    if (
      checkFunction === "is_not_null" ||
      checkFunction === "is_not_null_and_not_empty" ||
      checkFunction === "mandatory"
    ) {
      return "mandatory";
    }
    // DuckDB's Tier 1/2 pattern checks (regex/length).
    if (
      checkFunction === "regex_match" ||
      checkFunction === "max_length" ||
      checkFunction === "min_length" ||
      checkFunction === "length_range" ||
      checkFunction === "alphanumeric_only"
    ) {
      return "pattern";
    }
    // DQX's generated length checks all use "sql_expression", encoding the shape only in
    // the rule name suffix -- otherwise a field with both a pattern and length constraint
    // loses the length violation to "other".
    if (
      checkFunction === "sql_expression" &&
      (ruleName.endsWith("_invalid_length") ||
        ruleName.endsWith("_too_short") ||
        ruleName.endsWith("_too_long"))
    ) {
      return "pattern";
    }
    // Last resort when contract-rules metadata fails to load; a field's `rangeRuleType`
    // can normally override this to "conditional", which this fallback can't express.
    if (checkFunction === "value_range") {
      return "data type";
    }
    return "other";
  }
  const n = normalizeRuleType(ruleType);
  if (n === "data_type" || n === "data type" || n === "datatype" || n == "binary")
    return "data type";
  if (n === "mandatory") return "mandatory";
  if (n === "summation") return "summation";
  if (n === "conditional") return "conditional";
  if (n === "duplicates") return "duplicates";
  if (n === "pattern") return "pattern";
  if (n === "reference_list" || n === "reference list" || n === "referencelist")
    return "reference list";
  return "other";
}

/** @deprecated Prefer `buildContractRuleLookup` — keys are normalized rule names. */
export function buildRuleNameToRuleType(
  contractRules: Record<string, unknown>[]
): Map<string, string> {
  const lookup = buildContractRuleLookup(contractRules);
  const m = new Map<string, string>();
  for (const [k, meta] of lookup.entries()) {
    m.set(k, meta.rule_type);
  }
  return m;
}

/**
 * Shared scan: failing row indices + per-bucket row sets.
 *
 * When contract rules are still loading (`contractRuleLookup === undefined`):
 * - Summary mode: no breakdown buckets (all zeros) — same as legacy behavior.
 * - Filter mode: only rows with no named rules in errors/warnings go to `other` (for partial filtering).
 */
function computeDqxQuarantineRowBuckets(
  quarantineTable: Record<string, unknown>[],
  totalRowsChecked: number,
  contractRuleLookup: Map<string, DqxContractRuleMeta> | null | undefined,
  partialOtherWhileContractLoading: boolean,
  rowIndexBase = 0
): {
  failingRowIndices: Set<number>;
  bucketSets: Record<DqxSummaryCheckTypeBucket, Set<number>>;
} {
  const rowCount = Math.max(0, totalRowsChecked);
  const failingRowIndices = new Set<number>();
  const bucketSets: Record<DqxSummaryCheckTypeBucket, Set<number>> = {
    "data type": new Set(),
    mandatory: new Set(),
    summation: new Set(),
    conditional: new Set(),
    duplicates: new Set(),
    pattern: new Set(),
    "reference list": new Set(),
    other: new Set(),
  };

  const mapForBuckets =
    contractRuleLookup === undefined
      ? undefined
      : (contractRuleLookup ?? new Map<string, DqxContractRuleMeta>());

  quarantineTable.forEach((rawRow, quarantineTableIndex) => {
    const row = rawRow;
    const rowIndex = resolveQuarantineWorksheetRowIndex(
      row,
      rowCount,
      quarantineTableIndex,
      rowIndexBase
    );
    if (rowIndex < 0) return;

    const errors = parseQuarantineRulesArray(
      getQuarantineRowField(row, "errors", "ERRORS", "error")
    );
    const warnings = parseQuarantineRulesArray(
      getQuarantineRowField(row, "warnings", "WARNINGS", "warning")
    );
    if (errors.length === 0 && warnings.length === 0) return;

    failingRowIndices.add(rowIndex);

    if (mapForBuckets === undefined) {
      if (partialOtherWhileContractLoading) {
        const ruleNames = new Set<string>();
        for (const r of [...errors, ...warnings]) {
          const nm = typeof r.name === "string" ? r.name.trim() : "";
          if (nm) ruleNames.add(nm);
        }
        if (ruleNames.size === 0) {
          bucketSets.other.add(rowIndex);
        }
      }
      return;
    }

    const rules = [...errors, ...warnings];
    if (rules.length === 0) {
      bucketSets.other.add(rowIndex);
      return;
    }

    const bucketsForThisRow = new Set<DqxSummaryCheckTypeBucket>();
    let sawRuleSignal = false;
    for (const rule of rules) {
      const name = typeof rule.name === "string" ? rule.name.trim() : "";
      const fallbackFunction = typeof rule.function === "string" ? rule.function.trim() : "";
      const meta = name ? mapForBuckets.get(normalizeDqxRuleName(name)) : undefined;
      if (!name && !fallbackFunction && !meta?.check_function) continue;
      sawRuleSignal = true;
      bucketsForThisRow.add(
        mapContractRuleTypeToBucket(meta?.rule_type, {
          ruleName: name,
          checkFunction: meta?.check_function ?? fallbackFunction,
        })
      );
    }
    if (!sawRuleSignal) bucketsForThisRow.add("other");
    for (const b of bucketsForThisRow) {
      bucketSets[b].add(rowIndex);
    }
  });

  return { failingRowIndices, bucketSets };
}

/**
 * Compute summary from quarantine detail rows + optional contract rule name→type map.
 *
 * - `contractRuleLookup === undefined`: contract rules still loading — headline counts are valid;
 *   `failingRowsByCheckType` is all zeros.
 * - `contractRuleLookup === null`: load failed — same as empty map (untyped rules bucket to `other`).
 */
export function computeDqxRowLevelSummary(
  quarantineTable: Record<string, unknown>[],
  totalRowsChecked: number,
  contractRuleLookup: Map<string, DqxContractRuleMeta> | null | undefined,
  rowIndexBase = 0
): DqxRowLevelSummary {
  const rowCount = Math.max(0, totalRowsChecked);
  const { failingRowIndices, bucketSets } = computeDqxQuarantineRowBuckets(
    quarantineTable,
    totalRowsChecked,
    contractRuleLookup,
    false,
    rowIndexBase
  );

  const failingRowsWithAtLeastOneIssue = failingRowIndices.size;
  const rowsPassing = Math.max(0, rowCount - failingRowsWithAtLeastOneIssue);

  const failingRowsByCheckType = {} as Record<DqxSummaryCheckTypeBucket, number>;
  for (const key of DQX_SUMMARY_CHECK_TYPE_ORDER) {
    failingRowsByCheckType[key] = bucketSets[key].size;
  }

  return {
    totalRowsChecked: rowCount,
    rowsPassingAllChecks: rowsPassing,
    rowsWithAtLeastOneFailure: failingRowsWithAtLeastOneIssue,
    failingRowsByCheckType,
  };
}

/**
 * Row indices per check-type bucket for filtering the conformance grid.
 * While contract rules load (`contractRuleLookup === undefined`), only `other` may be populated
 * (rows with issues but no named rules).
 */
export function computeDqxRowIndicesByCheckType(
  quarantineTable: Record<string, unknown>[],
  totalRowsChecked: number,
  contractRuleLookup: Map<string, DqxContractRuleMeta> | null | undefined,
  rowIndexBase = 0
): Record<DqxSummaryCheckTypeBucket, Set<number>> {
  return computeDqxQuarantineRowBuckets(
    quarantineTable,
    totalRowsChecked,
    contractRuleLookup,
    true,
    rowIndexBase
  ).bucketSets;
}
